import React, { useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuthorization } from '../../hooks/useAuthorization';
import { useResponsive } from '../../hooks/useResponsive';
import { useModal } from '../../hooks/useModal';
import { Role } from '../../types';
import { navItemStyles, iconButtonStyles, overlayStyles } from '../../utils/classNames';
import { getFullVersion } from '../../config/version';
import {
  ChartPieIcon,
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  DocumentTextIcon,
  PresentationChartLineIcon,
  CalendarDaysIcon,
  TagIcon,
  XMarkIcon,
  CogIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ServerIcon,
  TruckIcon,
  RectangleGroupIcon,
} from '@heroicons/react/24/outline';

const NavItem: React.FC<{
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}> = ({ to, icon, label, onClick }) => (
  <NavLink to={to} onClick={onClick} className={({ isActive }) => navItemStyles(isActive)}>
    {icon}
    <span className='ml-3'>{label}</span>
  </NavLink>
);

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { checkPermission } = useAuthorization();
  const { isMobile } = useResponsive();
  const sidebarRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const canViewMaster = checkPermission([Role.ADMIN]);
  const canViewCases = checkPermission([Role.ADMIN, Role.DISPATCHER, Role.DRIVER]);
  // const canRunDispatch = checkPermission([Role.ADMIN, Role.DISPATCHER]);
  const canViewSchedule = checkPermission([Role.ADMIN, Role.DISPATCHER, Role.DRIVER]);
  const canViewReports = checkPermission([
    Role.ADMIN,
    Role.DISPATCHER,
    Role.READ_ONLY,
    Role.DRIVER,
  ]);
  const isSuperAdmin = user?.role === Role.SUPER_ADMIN;

  const iconClass = 'h-6 w-6';

  // 新しいuseModalフックで統一的にモーダル動作を管理
  const { handleOverlayClick } = useModal({
    isOpen: isMobile && isOpen,
    onClose,
    containerRef: sidebarRef,
    initialFocusRef: closeButtonRef,
    lockBodyScroll: true,
    closeOnEscape: true,
    closeOnOverlayClick: true,
  });

  const handleNavItemClick = () => {
    // モバイルでナビゲーション項目をクリックした時はサイドバーを閉じる
    if (isMobile) {
      onClose();
    }
  };

  const sidebarContent = (
    <>
      <div className='flex-grow overflow-y-auto'>
        {/* モバイル時のクローズボタン */}
        {isMobile && (
          <div className='flex justify-between items-center mb-4'>
            <div className='flex items-center'>
              <ChartPieIcon className='h-8 w-8 text-primary-600' />
              <span className='ml-2 text-xl font-bold text-gray-800 dark:text-white'>ラクハイ</span>
            </div>
            <button
              ref={closeButtonRef}
              onClick={onClose}
              className={iconButtonStyles('ghost', '44')}
              aria-label='サイドバーを閉じる'
            >
              <XMarkIcon className='h-6 w-6 text-gray-600 dark:text-gray-300' />
            </button>
          </div>
        )}

        {/* デスクトップ時のロゴ */}
        {!isMobile && (
          <div className='flex items-center mb-8'>
            <ChartPieIcon className='h-8 w-8 text-primary-600' />
            <span className='ml-2 text-xl font-bold text-gray-800 dark:text-white'>ラクハイ</span>
          </div>
        )}

        <nav className='flex flex-col space-y-2'>
          <NavItem
            to='/'
            icon={<HomeIcon className={iconClass} />}
            label='ダッシュボード'
            onClick={handleNavItemClick}
          />

          {/* Super Admin専用メニュー */}
          {isSuperAdmin && (
            <div>
              <h3 className='px-4 mt-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider'>
                管理者メニュー
              </h3>
              <div className='space-y-2'>
                <NavItem
                  to='/admin/companies'
                  icon={<BuildingOfficeIcon className={iconClass} />}
                  label='陸送会社'
                  onClick={handleNavItemClick}
                />
                <NavItem
                  to='/admin/all-users'
                  icon={<UsersIcon className={iconClass} />}
                  label='ユーザー'
                  onClick={handleNavItemClick}
                />
                <NavItem
                  to='/admin/business-partners'
                  icon={<UserGroupIcon className={iconClass} />}
                  label='取引先マスタ'
                  onClick={handleNavItemClick}
                />
                <NavItem
                  to='/master/vehicle-types'
                  icon={<RectangleGroupIcon className={iconClass} />}
                  label='車種'
                  onClick={handleNavItemClick}
                />
                <NavItem
                  to='/master/vehicle-models'
                  icon={<TruckIcon className={iconClass} />}
                  label='車両モデル'
                  onClick={handleNavItemClick}
                />
              </div>

              {/* システム監視 */}
              <h3 className='px-4 mt-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider'>
                システム監視
              </h3>
              <div className='space-y-2'>
                <NavItem
                  to='/admin/audit-logs'
                  icon={<DocumentTextIcon className={iconClass} />}
                  label='監査ログ'
                  onClick={handleNavItemClick}
                />
                <NavItem
                  to='/admin/system-health'
                  icon={<ChartBarIcon className={iconClass} />}
                  label='システム健全性'
                  onClick={handleNavItemClick}
                />
                <NavItem
                  to='/admin/backup-status'
                  icon={<ServerIcon className={iconClass} />}
                  label='バックアップ状況'
                  onClick={handleNavItemClick}
                />
              </div>
            </div>
          )}

          {canViewMaster && (
            <div>
              <h3 className='px-4 mt-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider'>
                マスター管理
              </h3>
              <div className='space-y-2'>
                <NavItem
                  to='/master/clients'
                  icon={<UserGroupIcon className={iconClass} />}
                  label='取引先'
                  onClick={handleNavItemClick}
                />
                <NavItem
                  to='/master/land-transporters'
                  icon={<UsersIcon className={iconClass} />}
                  label='陸送員'
                  onClick={handleNavItemClick}
                />
                <NavItem
                  to='/master/vehicle-models'
                  icon={<TagIcon className={iconClass} />}
                  label='車両モデル'
                  onClick={handleNavItemClick}
                />
              </div>
            </div>
          )}

          {canViewCases && (
            <div>
              <h3 className='px-4 mt-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider'>
                業務管理
              </h3>
              {user?.role === Role.DRIVER ? (
                <NavItem
                  to='/driver/cases'
                  icon={<DocumentTextIcon className={iconClass} />}
                  label='配車案件'
                  onClick={handleNavItemClick}
                />
              ) : (
                <NavItem
                  to='/cases'
                  icon={<DocumentTextIcon className={iconClass} />}
                  label='案件管理'
                  onClick={handleNavItemClick}
                />
              )}
              {canViewSchedule && (
                <>
                  <NavItem
                    to='/schedule'
                    icon={<CalendarDaysIcon className={iconClass} />}
                    label={user?.role === Role.DRIVER ? 'スケジュール' : '陸送員スケジュール'}
                    onClick={handleNavItemClick}
                  />
                  {(user?.role === Role.DRIVER || user?.role === Role.LAND_TRANSPORTER) && (
                    <NavItem
                      to='/schedule/register'
                      icon={<CalendarDaysIcon className={iconClass} />}
                      label='マイスケジュール登録'
                      onClick={handleNavItemClick}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {canViewReports && user?.role !== Role.DRIVER && (
            <div>
              <h3 className='px-4 mt-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider'>
                分析
              </h3>
              <NavItem
                to='/reports'
                icon={<PresentationChartLineIcon className={iconClass} />}
                label='レポート'
                onClick={handleNavItemClick}
              />
            </div>
          )}
        </nav>
      </div>

      {/* システム管理セクション（管理者のみ） */}
      {canViewMaster && (
        <div className='flex-shrink-0 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700'>
          <h3 className='px-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider'>
            システム管理
          </h3>
          <div className='space-y-2'>
            <NavItem
              to='/admin/schedules'
              icon={<CalendarDaysIcon className={iconClass} />}
              label='ドライバースケジュール管理'
              onClick={handleNavItemClick}
            />
            <NavItem
              to='/admin/settings'
              icon={<CogIcon className={iconClass} />}
              label='システム設定'
              onClick={handleNavItemClick}
            />
            <NavItem
              to='/admin/users'
              icon={<UsersIcon className={iconClass} />}
              label='ユーザー管理'
              onClick={handleNavItemClick}
            />
            <NavItem
              to='/admin/account'
              icon={<UserCircleIcon className={iconClass} />}
              label='アカウント'
              onClick={handleNavItemClick}
            />
          </div>
        </div>
      )}

      {/* バージョン情報（管理者のみ表示） */}
      {(canViewMaster || isSuperAdmin) && (
        <div className='flex-shrink-0 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700'>
          <div className='px-4 text-xs text-gray-500 dark:text-gray-400'>
            <div className='flex items-center justify-between'>
              <span>バージョン</span>
              <span className='font-mono'>{getFullVersion()}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );

  // モバイル表示 (スライドイン + オーバーレイ)
  if (isMobile) {
    return (
      <>
        {/* オーバーレイ */}
        {isOpen && (
          <div className={overlayStyles()} onClick={handleOverlayClick} aria-hidden='true' />
        )}

        {/* サイドバー */}
        <aside
          ref={sidebarRef}
          className={`fixed left-0 top-0 h-full w-80 max-w-[85vw] bg-white dark:bg-gray-800 shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col p-4 ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
          role='navigation'
          aria-label='メインナビゲーション'
        >
          {sidebarContent}
        </aside>
      </>
    );
  }

  // デスクトップ表示 (従来通り)
  return (
    <aside
      className='w-64 bg-white dark:bg-gray-800 shadow-md flex flex-col p-4'
      role='navigation'
      aria-label='メインナビゲーション'
    >
      {sidebarContent}
    </aside>
  );
};

export default Sidebar;
